import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { BedDouble, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { getCursorPage, post, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { cn, humanize, money } from '@/lib/utils';
import { DataTable, Dialog, EmptyState } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Input, Select, StatusBadge, Switch, TableSkeleton } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { orgReturnTo } from './org-types';
import { codeEntry, useBedCharges, useRefreshWards, type BedRow, type BranchOption, type WardRow } from './ward-types';

function bedStatusVariant(status: string): 'success' | 'warning' | 'secondary' {
  if (status === 'vacant') return 'success';
  if (status === 'occupied') return 'warning';
  return 'secondary';
}

const bedCount = (ward: WardRow) => ward.rooms.reduce((sum, room) => sum + room.beds.filter((bed) => bed.isActive).length, 0);

const WARD_PAGE_CAP = 25;

const retiredLook = (bed: BedRow) => (bed.isActive ? undefined : 'text-muted-foreground opacity-60');

export function WardsAndBedsTab({ branches }: { branches: BranchOption[] }) {
  const { can, canWrite } = useAuth();
  const navigate = useNavigate();
  const manage = can('bed.manage') && canWrite;
  const workingBranchId = useWorkingBranchId();
  const [branchId, setBranchId] = useState(() => (branches.some((branch) => branch.id === workingBranchId) ? workingBranchId : ''));
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [addingRoomTo, setAddingRoomTo] = useState<WardRow | null>(null);
  const openForm = (path: string, search: Record<string, string>) =>
    navigate(`${path}${qs({ ...search, returnTo: orgReturnTo('wards') })}`);
  const openWard = (ward: WardRow | null) =>
    openForm(ward ? `/admin/wards/${ward.id}/edit` : '/admin/wards/new', { branchId: ward?.branchId ?? branchId });
  const openBed = (ward: WardRow, roomId: string, bed: BedRow | null) =>
    openForm(bed ? `/admin/beds/${bed.id}/edit` : '/admin/beds/new', { wardId: ward.id, branchId: ward.branchId, roomId });

  const wards = useInfiniteQuery({
    queryKey: ['admin-wards', branchId, manage],
    initialPageParam: '',
    queryFn: ({ pageParam, signal }) =>
      getCursorPage<WardRow>(
        `/ipd/wards${qs({ branchId: branchId || undefined, includeInactive: manage ? 1 : undefined, cursor: pageParam || undefined })}`,
        signal,
      ),
    getNextPageParam: (last, pages) => (last.nextCursor && pages.length < WARD_PAGE_CAP ? last.nextCursor : undefined),
  });
  const wardRows = wards.data?.pages.flatMap((page) => page.rows) ?? [];
  const chargesReadable = can('charge.read');
  const bedCharges = useBedCharges(chargesReadable);
  const chargeById = new Map((bedCharges.data ?? []).map((item) => [item.id, item]));
  const branchName = (id: string) => branches.find((branch) => branch.id === id)?.name ?? '—';

  const toggle = (wardId: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(wardId)) next.delete(wardId);
      else next.add(wardId);
      return next;
    });
  const reveal = (wardId: string) => setExpanded((current) => new Set(current).add(wardId));

  const chargeLabel = (bed: BedRow) => {
    if (!bed.chargeItemId) return <span className="text-muted-foreground">—</span>;
    const charge = chargeById.get(bed.chargeItemId);
    return charge ? `${charge.name} · ${money(charge.basePriceMinor)}` : tr('admin', 'set');
  };

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select value={branchId} onChange={(e) => setBranchId(e.target.value)} className="w-64" aria-label={tr('admin', 'branch')}>
          <option value="">{tr('admin', 'all_branches')}</option>
          {branches.map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.name}
            </option>
          ))}
        </Select>
      </div>

      {wards.isLoading ? (
        <TableSkeleton />
      ) : wards.error ? (
        <Alert variant="destructive">{describeError(wards.error)}</Alert>
      ) : wardRows.length === 0 ? (
        <EmptyState
          icon={BedDouble}
          title={tr('admin', 'no_wards_yet')}
          description={tr('admin', 'no_wards_yet_hint')}
          action={manage ? <Button onClick={() => openWard(null)}>{tr('admin', 'add_ward')}</Button> : undefined}
        />
      ) : (
        <div className="space-y-3">
          {wardRows.map((ward) => {
            const open = expanded.has(ward.id);
            return (
              <section key={ward.id} className="rounded-lg border bg-card">
                <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => toggle(ward.id)}
                    aria-expanded={open}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded-md text-start focus-ring"
                  >
                    {open ? (
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground rtl:-scale-x-100" aria-hidden />
                    )}
                    <span className="font-mono text-xs text-muted-foreground">{ward.code}</span>
                    <span className="truncate font-medium">{ward.name}</span>
                    {ward.department && <Badge variant="secondary">{ward.department.name}</Badge>}
                    {!branchId && <span className="text-xs text-muted-foreground">{branchName(ward.branchId)}</span>}
                    <span className="text-xs text-muted-foreground">
                      {tr('admin', 'x_rooms_y_beds', { rooms: ward.rooms.length, beds: bedCount(ward) })}
                    </span>
                  </button>
                  {manage && (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          reveal(ward.id);
                          setAddingRoomTo(ward);
                        }}
                      >
                        <Plus className="me-1 h-3.5 w-3.5" /> {tr('admin', 'add_room')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openWard(ward)}>
                        {tr('admin', 'edit')}
                      </Button>
                    </div>
                  )}
                </div>

                {open && (
                  <div className="space-y-3 border-t bg-muted/30 p-3">
                    {ward.rooms.length === 0 && (
                      <p className="text-sm text-muted-foreground">{tr('admin', 'no_rooms_yet_add_one_to_place_beds')}</p>
                    )}
                    {ward.rooms.map((room) => (
                      <div key={room.id} className="rounded-md border bg-card">
                        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                          <p className="text-sm">
                            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{tr('admin', 'room')}</span>{' '}
                            <span className="font-mono text-xs">{room.code}</span>
                            {room.name && <span className="ms-2">{room.name}</span>}
                          </p>
                          {manage && (
                            <Button size="sm" variant="ghost" onClick={() => openBed(ward, room.id, null)}>
                              <Plus className="me-1 h-3.5 w-3.5" /> {tr('admin', 'add_bed')}
                            </Button>
                          )}
                        </div>
                        <div className="px-3 pb-3">
                          {room.beds.length === 0 ? (
                            <p className="text-sm text-muted-foreground">{tr('admin', 'no_beds_in_this_room_yet')}</p>
                          ) : (
                            <DataTable
                              dense
                              rows={room.beds}
                              rowKey={(bed) => bed.id}
                              columns={[
                                {
                                  header: tr('admin', 'code'),
                                  width: '110px',
                                  cell: (bed) => <span className={cn('font-mono text-xs', retiredLook(bed))}>{bed.code}</span>,
                                },
                                { header: tr('admin', 'bed_class'), cell: (bed) => <span className={retiredLook(bed)}>{humanize(bed.bedClass, 'bed_class')}</span> },
                                { header: tr('admin', 'daily_charge'), cell: (bed) => <span className={retiredLook(bed)}>{chargeLabel(bed)}</span> },
                                {
                                  header: tr('admin', 'status'),
                                  cell: (bed) =>
                                    bed.isActive ? (
                                      <StatusBadge status={bed.status} tone={bedStatusVariant(bed.status)} />
                                    ) : (
                                      <Badge variant="secondary" className="opacity-60">
                                        {tr('admin', 'bed_retired')}
                                      </Badge>
                                    ),
                                },
                                {
                                  header: tr('admin', 'in_service'),
                                  align: 'center',
                                  cell: (bed) => <BedServiceSwitch bed={bed} disabled={!manage} />,
                                },
                                {
                                  header: '',
                                  align: 'right',
                                  cell: (bed) =>
                                    manage ? (
                                      <Button size="sm" variant="outline" onClick={() => openBed(ward, room.id, bed)}>
                                        {tr('admin', 'edit')}
                                      </Button>
                                    ) : null,
                                },
                              ]}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
          {wards.hasNextPage && (
            <div className="flex justify-center">
              <Button variant="secondary" loading={wards.isFetchingNextPage} onClick={() => void wards.fetchNextPage()}>
                {tr('admin', 'load_more')}
              </Button>
            </div>
          )}
        </div>
      )}

      {addingRoomTo && <RoomDialog ward={addingRoomTo} onClose={() => setAddingRoomTo(null)} />}
    </>
  );
}

function BedServiceSwitch({ bed, disabled }: { bed: BedRow; disabled: boolean }) {
  const toast = useToast();
  const refresh = useRefreshWards();
  const [confirming, setConfirming] = useState(false);
  const change = useMutation({
    mutationFn: (isActive: boolean) => put(`/ipd/beds/${bed.id}`, { isActive }),
    onSuccess: (_, isActive) => {
      setConfirming(false);
      if (isActive) toast.success(tr('admin', 'bed_x_back_in_service', { code: bed.code }));
      else toast.success(tr('admin', 'bed_x_retired', { code: bed.code }), tr('admin', 'retired_beds_stay_here_greyed'));
      refresh();
    },
    onError: (caught) => {
      setConfirming(false);
      toast.error(tr('admin', 'could_not_change_the_bed'), describeError(caught));
    },
  });
  const shownActive = change.variables !== undefined && (change.isPending || change.isSuccess) ? change.variables : bed.isActive;

  return (
    <>
      <Switch
        checked={shownActive}
        disabled={disabled || change.isPending}
        aria-label={tr('admin', 'bed_x_in_service', { code: bed.code })}
        title={tr('admin', 'retiring_a_bed_takes_it_off_the_bed_board')}
        onCheckedChange={(checked) => {
          if (checked) change.mutate(true);
          else setConfirming(true);
        }}
      />
      {confirming && (
        <Dialog
          open
          onClose={() => setConfirming(false)}
          title={tr('admin', 'retire_bed_x', { code: bed.code })}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                {tr('admin', 'cancel')}
              </Button>
              <Button variant="destructive" loading={change.isPending} onClick={() => change.mutate(false)}>
                {tr('admin', 'retire_bed')}
              </Button>
            </>
          }
        >
          <p className="text-sm">{tr('admin', 'retire_bed_x_confirm', { code: bed.code })}</p>
        </Dialog>
      )}
    </>
  );
}

function RoomDialog({ ward, onClose }: { ward: WardRow; onClose: () => void }) {
  const toast = useToast();
  const refresh = useRefreshWards();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => post('/ipd/rooms', { wardId: ward.id, code, name: name.trim() || undefined }),
    onSuccess: () => {
      toast.success(tr('admin', 'room_created'));
      refresh();
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('admin', 'add_a_room_to_x', { name: ward.name })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('admin', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            loading={save.isPending}
            disabled={code.length < 1}
          >
            {tr('admin', 'create_room')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={tr('admin', 'code')} required hint={tr('admin', 'short_code_unique_within_the_ward')}>
          <Input value={code} maxLength={16} onChange={(e) => setCode(codeEntry(e.target.value))} />
        </Field>
        <Field label={tr('admin', 'name')} hint={tr('admin', 'optional_name_such_as_bay_a')}>
          <Input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </Field>
        {error && (
          <div className="sm:col-span-2">
            <Alert variant="destructive">{error}</Alert>
          </div>
        )}
      </div>
    </Dialog>
  );
}
